import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import styles from '../styles/UserPage.module.css';
import { API_PROVIDERS, PROVIDER_CREDENTIAL_FIELDS, MODEL_OPTIONS, providerBase } from '../utils/modelConstants';
import { buildConfiguredProviderOptions } from '../utils/providerOptions';
import { toast } from 'react-hot-toast';
import {
  emptyConfig,
  defaultProviderConfigs,
  isMasked,
  maskSavedConfig,
  CREDENTIAL_FIELDS,
  MAX_PERSONAS,
  normalizePersonasForState,
  DEFAULT_AGENT_ASSIGNMENT,
  DEFAULT_AGENT_NAMES,
  EMPTY_MODEL_SETTINGS,
  PLATFORM_MAIN_MODEL,
  agentsResetIn,
  describeKeyReset,
  isAgentDisabled,
  normalizeFieldValue,
} from '../utils/userPageHelpers';
import AccountTab from './subcomponents/AccountTab';
import ApiKeysTab from './subcomponents/ApiKeysTab';
import ConnectTab from './subcomponents/ConnectTab';
import ModelDefaultsTab from './subcomponents/ModelDefaultsTab';
import PersonasTab from './subcomponents/PersonasTab';
import TabNav from './subcomponents/TabNav';

export default function UserPage({ refreshHistories, resetMainPanel, refreshModelSettings }) {
  const { user, logout } = useAuth();

  const [providerConfigs, setProviderConfigs] = useState(defaultProviderConfigs());
  const [originalProviderConfigs, setOriginalProviderConfigs] = useState(defaultProviderConfigs());
  const [isLoading, setIsLoading] = useState(true);
  const [focusedInput, setFocusedInput] = useState(null);

  // Orchestrator Model State
  const [orchestratorProvider, setOrchestratorProvider] = useState('');
  const [originalOrchestratorProvider, setOriginalOrchestratorProvider] = useState('');
  const [orchestratorModel, setOrchestratorModel] = useState('');
  const [originalOrchestratorModel, setOriginalOrchestratorModel] = useState('');
  const [orchestratorModelName, setOrchestratorModelName] = useState('');
  const [orchestratorSettings, setOrchestratorSettings] = useState({
    ...EMPTY_MODEL_SETTINGS,
  });

  // Summarizer Model State — optional override for workflow synthesis.
  // Empty provider means "Use main model" (no override stored).
  const [summarizerProvider, setSummarizerProvider] = useState('');
  const [originalSummarizerProvider, setOriginalSummarizerProvider] = useState('');
  const [summarizerModel, setSummarizerModel] = useState('');
  const [originalSummarizerModel, setOriginalSummarizerModel] = useState('');

  // Agent Model Assignments State
  const [agentModelAssignments, setAgentModelAssignments] = useState({
    capo: { ...DEFAULT_AGENT_ASSIGNMENT },
    cardian: { ...DEFAULT_AGENT_ASSIGNMENT },
    aeri: { ...DEFAULT_AGENT_ASSIGNMENT },
  });
  const [originalAgentModelAssignments, setOriginalAgentModelAssignments] = useState({});
  // Model profiles: which profile this tab is editing (the active one) + the
  // registry list.  ``profileRefresh`` re-triggers the keys fetch after a
  // profile action so the form reflects the (new) active profile.
  const [profiles, setProfiles] = useState([]);
  const [activeProfileName, setActiveProfileName] = useState('Default');
  const [profileRefresh, setProfileRefresh] = useState(0);
  const [personas, setPersonas] = useState(normalizePersonasForState([]));
  const [originalPersonas, setOriginalPersonas] = useState(normalizePersonasForState([]));

  const [availableModels, setAvailableModels] = useState({});
  // The platform catalog by provider; null until it loads.
  const [modelCatalog, setModelCatalog] = useState(null);
  const [modelRecommended, setModelRecommended] = useState({});

  // Add Model Form State
  const [addModelProvider, setAddModelProvider] = useState('');
  const [addModelName, setAddModelName] = useState('');
  const [isAddingModel, setIsAddingModel] = useState(false);

  // Tool API Keys State
  // toolApiKeys: array of {tool_name, has_key} from GET /api/user/tool-api-keys
  // toolApiKeyInputs: {[tool_name]: string} — in-progress input values
  const [toolApiKeys, setToolApiKeys] = useState([]);
  const [toolApiKeyInputs, setToolApiKeyInputs] = useState({});

  // Privacy Preferences State
  const [optOutTraining, setOptOutTraining] = useState(false);
  const [optOutResearch, setOptOutResearch] = useState(false);
  const [originalOptOutTraining, setOriginalOptOutTraining] = useState(false);
  const [originalOptOutResearch, setOriginalOptOutResearch] = useState(false);

  // Delete All Conversations State
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const [isDeletingRawAudits, setIsDeletingRawAudits] = useState(false);

  // Connect Tab State
  const [connectDevices, setConnectDevices] = useState([]);
  const [connectLoading, setConnectLoading] = useState(false);
  const [connectLoaded, setConnectLoaded] = useState(false);
  const [isCreatingToken, setIsCreatingToken] = useState(false);
  const [isRevokingId, setIsRevokingId] = useState(null);

  // Tab State
  const [activeTab, setActiveTab] = useState('api-keys');
  const [selectedApiProvider, setSelectedApiProvider] = useState(API_PROVIDERS[0] || '');

  useEffect(() => {
    if (user?.id) {
      const fetchKeys = async () => {
        setIsLoading(true);
        try {
          const response = await fetch(
            `${process.env.REACT_APP_AUTH_BACKEND_URL}/api/user/api_keys?user_id=${user.id}`,
            { credentials: 'include' }
          );
          const data = await response.json();
          if (response.ok && data.status === 'ok') {

            if (data.active_profile_name) {
              setActiveProfileName(data.active_profile_name);
            }
            if (Array.isArray(data.profiles)) {
              setProfiles(data.profiles);
            }

            if (data.provider_configs && Object.keys(data.provider_configs).length > 0) {
              const merged = defaultProviderConfigs();
              for (const [prov, cfgList] of Object.entries(data.provider_configs)) {
                merged[prov] = cfgList.map(c => ({
                  config_index: c.config_index,
                  config_name: c.config_name || '',
                  api_key: c.api_key || '',
                  endpoint: c.endpoint || '',
                  access_key: c.access_key || '',
                  secret_key: c.secret_key || '',
                  region: c.region || '',
                }));
              }
              setProviderConfigs(merged);
              setOriginalProviderConfigs(JSON.parse(JSON.stringify(merged)));
              const firstConfiguredProvider = API_PROVIDERS.find(prov =>
                (merged[prov] || []).some(cfg => cfg.api_key || cfg.access_key || cfg.secret_key)
              );
              setSelectedApiProvider(prev => prev || firstConfiguredProvider || API_PROVIDERS[0] || '');
            }

            const defaultAssignments = {
              capo: { ...DEFAULT_AGENT_ASSIGNMENT },
              cardian: { ...DEFAULT_AGENT_ASSIGNMENT },
              aeri: { ...DEFAULT_AGENT_ASSIGNMENT },
            };
            if (data.agent_model_assignments && Object.keys(data.agent_model_assignments).length > 0) {
              const normalizedDbAssignments = Object.fromEntries(
                Object.entries(data.agent_model_assignments).map(([name, assignment]) => [
                  name,
                  {
                    provider: assignment?.provider || '',
                    model_name: assignment?.model_name || '',
                    persona_id: assignment?.persona_id || '',
                  },
                ])
              );
              const mergedAssignments = { ...defaultAssignments, ...normalizedDbAssignments };
              setAgentModelAssignments(mergedAssignments);
              setOriginalAgentModelAssignments(JSON.parse(JSON.stringify(mergedAssignments)));
            } else {
              setAgentModelAssignments(defaultAssignments);
              setOriginalAgentModelAssignments(JSON.parse(JSON.stringify(defaultAssignments)));
            }

            const personaRows = normalizePersonasForState(data.personas || []);
            setPersonas(personaRows);
            setOriginalPersonas(JSON.parse(JSON.stringify(personaRows)));

            if (data.available_models) setAvailableModels(data.available_models);
            if (data.orchestrator_model) {
              setOrchestratorProvider(data.orchestrator_model.provider || '');
              setOriginalOrchestratorProvider(data.orchestrator_model.provider || '');
              setOrchestratorModel(data.orchestrator_model.model || '');
              setOriginalOrchestratorModel(data.orchestrator_model.model || '');
              setOrchestratorModelName(data.orchestrator_model.name || '');
              setOrchestratorSettings({
                maxTokens: data.orchestrator_model.max_tokens || '',
                temperature: data.orchestrator_model.temperature || '',
                topP: data.orchestrator_model.top_p || '',
                timeout: data.orchestrator_model.timeout || '',
                presencePenalty: data.orchestrator_model.presence_penalty || '',
                frequencyPenalty: data.orchestrator_model.frequency_penalty || '',
              });

              const prov = data.orchestrator_model.provider || '';
              const modelFromDb = (data.available_models && data.available_models[providerBase(prov)]) || [];
              if (prov && prov !== 'default' && !data.orchestrator_model.model && (!modelFromDb || modelFromDb.length === 0)) {
                setAddModelProvider(prov);
                setAddModelName('');
                setOrchestratorModel('__ADD_NEW__');
              }
            }

            // Optional summarizer override; absent means "Use main model".
            if (data.summarizer_model) {
              setSummarizerProvider(data.summarizer_model.provider || '');
              setOriginalSummarizerProvider(data.summarizer_model.provider || '');
              setSummarizerModel(data.summarizer_model.model || '');
              setOriginalSummarizerModel(data.summarizer_model.model || '');
            } else {
              setSummarizerProvider('');
              setOriginalSummarizerProvider('');
              setSummarizerModel('');
              setOriginalSummarizerModel('');
            }
          } else {
            toast.error(data.detail || 'Failed to fetch API keys.');
          }
        } catch (err) {
          toast.error('An error occurred while fetching API keys.');
        }

        setIsLoading(false);
      };
      void fetchKeys();
    }
  }, [user, setOriginalAgentModelAssignments, profileRefresh]);

  // Global (not profile-scoped) data: privacy prefs, the provider/model
  // catalog, and tool keys.  Fetched once per user — a profile switch must
  // not needlessly re-pull these (#17).
  useEffect(() => {
    if (!user?.id) return;
    const fetchGlobal = async () => {
      try {
        const prefResponse = await fetch(
          `${process.env.REACT_APP_AUTH_BACKEND_URL}/api/user/privacy_preferences`,
          { credentials: 'include' }
        );
        const prefData = await prefResponse.json();
        if (prefResponse.ok && prefData.status === 'ok') {
          setOptOutTraining(prefData.preferences.opt_out_training || false);
          setOptOutResearch(prefData.preferences.opt_out_research || false);
          setOriginalOptOutTraining(prefData.preferences.opt_out_training || false);
          setOriginalOptOutResearch(prefData.preferences.opt_out_research || false);
        }
      } catch (err) {
        console.error('Failed to fetch privacy preferences:', err);
      }

      try {
        const catalogRes = await fetch(
          `${process.env.REACT_APP_AUTH_BACKEND_URL}/api/provider_models`,
          { credentials: 'include' }
        );
        const catalogData = await catalogRes.json();
        if (catalogRes.ok && catalogData.status === 'ok') {
          setModelCatalog(catalogData.catalog || {});
          setModelRecommended(catalogData.recommended || {});
        }
      } catch (err) {
        console.error('Failed to fetch provider model catalog:', err);
      }

      try {
        const toolKeyRes = await fetch(
          `${process.env.REACT_APP_AUTH_BACKEND_URL}/api/user/tool-api-keys`,
          { credentials: 'include' }
        );
        const toolKeyData = await toolKeyRes.json();
        if (toolKeyRes.ok && toolKeyData.tool_api_keys) {
          setToolApiKeys(toolKeyData.tool_api_keys);
        }
      } catch (err) {
        console.error('Failed to fetch tool API keys:', err);
      }
    };
    void fetchGlobal();
  }, [user]);

  // ── Model-profile actions (call the API, then reload this tab) ────────
  const _profilePost = async (path, body) => {
    const res = await fetch(
      `${process.env.REACT_APP_AUTH_BACKEND_URL}${path}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body || {}),
      },
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.status !== 'ok') {
      throw new Error(data.detail || 'Profile action failed');
    }
    return data;
  };

  const _afterProfileChange = () => {
    setProfileRefresh((n) => n + 1);
    if (refreshModelSettings) refreshModelSettings();
  };

  const switchProfile = async (name) => {
    await _profilePost('/api/user/active-profile', { profile_name: name });
    _afterProfileChange();
  };

  const createProfile = async (
    name,
    { duplicateFrom = null, activate = false } = {},
  ) => {
    await _profilePost('/api/user/profiles', {
      profile_name: name,
      duplicate_from: duplicateFrom,
      activate,
    });
    _afterProfileChange();
  };

  const renameProfile = async (oldName, newName) => {
    await _profilePost('/api/user/profiles/rename', {
      profile_name: oldName,
      new_name: newName,
    });
    _afterProfileChange();
  };

  const deleteProfile = async (name) => {
    await _profilePost('/api/user/profiles/delete', { profile_name: name });
    _afterProfileChange();
  };

  // Unsaved edits in the Model Defaults tab — switching profiles reloads and
  // would discard them, so the switcher confirms first (#12).
  const modelDefaultsDirty =
    orchestratorProvider !== originalOrchestratorProvider ||
    orchestratorModel !== originalOrchestratorModel ||
    summarizerProvider !== originalSummarizerProvider ||
    summarizerModel !== originalSummarizerModel ||
    JSON.stringify(agentModelAssignments) !==
      JSON.stringify(originalAgentModelAssignments);

  const handleConfigFieldChange = (provider, configIndex, field, value) => {
    const normalizedValue = normalizeFieldValue(field, value);
    setProviderConfigs(prev => ({
      ...prev,
      [provider]: (prev[provider] || []).map(cfg => {
        if (cfg.config_index === configIndex) {
          const updatedCfg = { ...cfg, [field]: normalizedValue };
          const originalCfg = (originalProviderConfigs[provider] || []).find(c => c.config_index === configIndex);
          const originalValue = originalCfg ? originalCfg[field] : '';
          if (isMasked(originalValue) && isMasked(updatedCfg[field])) {
            updatedCfg[field] = originalValue;
          } else if (isMasked(originalValue) && !isMasked(updatedCfg[field]) && normalizedValue === originalValue) {
            updatedCfg[field] = originalValue;
          }
          return updatedCfg;
        }
        return cfg;
      }),
    }));
  };

  const handleClearConfigField = (provider, configIndex, field) => {
    handleConfigFieldChange(provider, configIndex, field, '');
    toast.success(`Field cleared`);
  };

  const handleAddProviderConfig = (provider) => {
    setProviderConfigs(prev => {
      const existing = prev[provider] || [];
      const nextIndex = existing.length > 0
        ? Math.max(...existing.map(c => c.config_index)) + 1
        : 2;
      return {
        ...prev,
        [provider]: [...existing, emptyConfig(nextIndex)],
      };
    });
  };

  const handleDeleteProviderConfig = async (provider, configIndex) => {
    setProviderConfigs(prev => ({
      ...prev,
      [provider]: (prev[provider] || []).filter(cfg => cfg.config_index !== configIndex),
    }));
    try {
      const response = await fetch(
        `${process.env.REACT_APP_AUTH_BACKEND_URL}/api/user/provider_config`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ provider, config_index: configIndex }),
        }
      );
      const data = await response.json();
      if (!response.ok || data.status !== 'ok') {
        toast.error(data.detail || 'Failed to delete provider config');
        return;
      }

      // Only this tab's profile: the reply also lists other profiles' agents.
      const { reset: defaultsReset, cleared: customCleared } = agentsResetIn(
        data, activeProfileName,
      );
      if (defaultsReset.length || customCleared.length) {
        setAgentModelAssignments(prev => {
          const next = { ...prev };
          defaultsReset.forEach(agentName => {
            next[agentName] = {
              provider: 'stackform',
              model_name: 'default-rapid-model',
              persona_id: next[agentName]?.persona_id || '',
            };
          });
          customCleared.forEach(agentName => {
            next[agentName] = {
              provider: '',
              model_name: '',
              persona_id: next[agentName]?.persona_id || '',
            };
          });
          setOriginalAgentModelAssignments(JSON.parse(JSON.stringify(next)));
          return next;
        });
      }
      // The server reset this tab's profile too: show it as saved.
      if ((data.main_models_reset || []).includes(activeProfileName)) {
        setOrchestratorProvider(PLATFORM_MAIN_MODEL.provider);
        setOriginalOrchestratorProvider(PLATFORM_MAIN_MODEL.provider);
        setOrchestratorModel(PLATFORM_MAIN_MODEL.model);
        setOriginalOrchestratorModel(PLATFORM_MAIN_MODEL.model);
        setOrchestratorSettings({ ...EMPTY_MODEL_SETTINGS });
      }
      if ((data.summarizer_models_cleared || []).includes(activeProfileName)) {
        setSummarizerProvider('');
        setOriginalSummarizerProvider('');
        setSummarizerModel('');
        setOriginalSummarizerModel('');
      }
      const resetMessage = describeKeyReset(data);
      if (resetMessage) {
        if (refreshModelSettings) refreshModelSettings();
        toast.success(resetMessage);
      }
    } catch (e) {
      console.error('Failed to delete provider config', e);
      toast.error('Failed to delete provider config');
    }
  };

  const handleSaveToolApiKey = async (toolName) => {
    const value = (toolApiKeyInputs[toolName] || '').trim();
    if (!value) {
      toast.error('Please enter an API key before saving.');
      return;
    }
    try {
      const res = await fetch(
        `${process.env.REACT_APP_AUTH_BACKEND_URL}/api/user/tool-api-keys`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ tool_name: toolName, api_key: value }),
        }
      );
      const data = await res.json();
      if (!res.ok || data.status !== 'ok') {
        toast.error(data.detail || 'Failed to save tool API key.');
        return;
      }
      setToolApiKeys(prev =>
        prev.map(t =>
          t.tool_name === toolName ? { ...t, has_key: true } : t
        )
      );
      setToolApiKeyInputs(prev => ({ ...prev, [toolName]: '' }));
      toast.success('Tool API key saved.');
    } catch (e) {
      console.error('Failed to save tool API key', e);
      toast.error('Failed to save tool API key.');
    }
  };

  const handleRemoveToolApiKey = async (toolName) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_AUTH_BACKEND_URL}/api/user/tool-api-keys/${toolName}`,
        { method: 'DELETE', credentials: 'include' }
      );
      if (!res.ok) {
        toast.error('Failed to remove tool API key.');
        return;
      }
      setToolApiKeys(prev =>
        prev.map(t =>
          t.tool_name === toolName ? { ...t, has_key: false } : t
        )
      );
      setToolApiKeyInputs(prev => ({ ...prev, [toolName]: '' }));
      toast.success('Tool API key removed.');
    } catch (e) {
      console.error('Failed to remove tool API key', e);
      toast.error('Failed to remove tool API key.');
    }
  };

  const handleOrchestratorProviderChange = (e) => {
    const newProvider = e.target.value;
    setOrchestratorProvider(newProvider);

    let defaultModel;
    if (originalOrchestratorProvider === newProvider && originalOrchestratorModel) {
      defaultModel = originalOrchestratorModel;
      setAddModelProvider('');
    } else if (newProvider === 'default' && MODEL_OPTIONS.default && MODEL_OPTIONS.default.length > 0) {
      defaultModel = MODEL_OPTIONS.default[0].value;
      setAddModelProvider('');
    } else {
      defaultModel = '';
      setAddModelProvider('');
    }
    setOrchestratorModel(defaultModel);
  };

  const handleSummarizerProviderChange = (e) => {
    const newProvider = e.target.value;
    setSummarizerProvider(newProvider);

    let defaultModel;
    if (originalSummarizerProvider === newProvider && originalSummarizerModel) {
      defaultModel = originalSummarizerModel;
    } else if (newProvider === 'default' && MODEL_OPTIONS.default && MODEL_OPTIONS.default.length > 0) {
      defaultModel = MODEL_OPTIONS.default[0].value;
    } else {
      // Empty provider == "Use main model"; clear any selected model.
      defaultModel = '';
    }
    setSummarizerModel(defaultModel);
  };

  const makePersonaId = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `persona-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  };

  const handlePersonaFieldChange = (index, field, value) => {
    setPersonas(prev => prev.map((persona, i) => {
      if (i !== index) return persona;
      const next = { ...persona, [field]: value };
      const hasContent = (next.title || '').trim() || (next.prompt || '').trim();
      if (!next.id && hasContent) {
        next.id = makePersonaId();
      }
      return next;
    }));
  };

  const handleResetPersonasToDefault = async () => {
    if (!window.confirm('Reset personas 1-3 to the application defaults? This ' +
        'will overwrite your current persona entries.')) {
      return;
    }
    try {
      const response = await fetch(
        `${process.env.REACT_APP_AUTH_BACKEND_URL}/api/user/personas/reset_defaults`,
        { method: 'POST', credentials: 'include' }
      );
      const data = await response.json();
      if (response.ok && data.status === 'ok') {
        const normalized = normalizePersonasForState(data.personas || []);
        setPersonas(normalized);
        setOriginalPersonas(JSON.parse(JSON.stringify(normalized)));
        if (refreshModelSettings) refreshModelSettings();
        toast.success('Personas reset to defaults.');
      } else {
        toast.error(data.detail || 'Failed to reset personas.');
      }
    } catch (err) {
      toast.error('An error occurred while resetting personas.');
    }
  };

  const handleResetSinglePersonaToDefault = async (index) => {
    if (!window.confirm(`Reset persona ${index + 1} to the application default? This ` +
        'will overwrite your current persona entry.')) {
      return;
    }
    try {
      const response = await fetch(
        `${process.env.REACT_APP_AUTH_BACKEND_URL}/api/user/personas/reset_single_default`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ persona_index: index + 1 }),
        }
      );
      const data = await response.json();
      if (response.ok && data.status === 'ok') {
        const normalized = normalizePersonasForState(data.personas || []);
        setPersonas(normalized);
        setOriginalPersonas(JSON.parse(JSON.stringify(normalized)));
        if (refreshModelSettings) refreshModelSettings();
        toast.success(`Persona ${index + 1} reset to default.`);
      } else {
        toast.error(data.detail || `Failed to reset persona ${index + 1}.`);
      }
    } catch (err) {
      toast.error(`An error occurred while resetting persona ${index + 1}.`);
    }
  };

  const handleRemovePersona = async (index) => {
    if (!window.confirm(`Remove persona ${index + 1}? This cannot be undone.`)) {
      return;
    }
    try {
      const payload = personas
        .filter((p, i) => i !== index && (p.title || '').trim() && (p.prompt || '').trim())
        .map((p, i) => ({
          id: p.id || '',
          sort_order: p.sort_order ?? (i + 1),
          title: (p.title || '').trim(),
          prompt: (p.prompt || '').trim(),
        }));

      const response = await fetch(
        `${process.env.REACT_APP_AUTH_BACKEND_URL}/api/user/personas`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ personas: payload }),
        }
      );
      const data = await response.json();
      if (response.ok && data.status === 'ok') {
        const normalized = normalizePersonasForState(data.personas || []);
        setPersonas(normalized);
        setOriginalPersonas(JSON.parse(JSON.stringify(normalized)));
        if (refreshModelSettings) refreshModelSettings();
        toast.success(`Persona ${index + 1} removed.`);
      } else {
        toast.error(data.detail || `Failed to remove persona ${index + 1}.`);
      }
    } catch (err) {
      toast.error(`An error occurred while removing persona ${index + 1}.`);
    }
  };

  // onSuccess(newModelName) — called after a successful add.
  // When omitted the orchestrator model is updated (default flow).
  const handleAddModel = async (
    providerArg = null,
    modelArg = null,
    onSuccess = null,
  ) => {
    const provider = providerArg ?? addModelProvider;
    const model = (modelArg ?? addModelName).trim();

    if (!provider || !model) {
      toast.error('Please select a provider and enter a model name');
      return;
    }

    // Only show the shared spinner for the orchestrator form.
    if (!providerArg) setIsAddingModel(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_AUTH_BACKEND_URL}/api/user/add_model`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ provider, model }),
        }
      );

      const data = await response.json();
      if (response.ok && data.status === 'ok') {
        toast.success(data.message || 'Model added successfully');
        // The reply carries the updated lists, keyed by provider family.
        if (data.available_models) setAvailableModels(data.available_models);

        if (onSuccess) {
          onSuccess(model);
        } else {
          // Orchestrator flow: select the new model and close the form.
          setOrchestratorModel(model);
          setAddModelName('');
          setAddModelProvider('');
        }

        if (refreshModelSettings) refreshModelSettings();
      } else {
        // A 422's detail is a list of validation errors, not a message.
        toast.error(
          typeof data.detail === 'string' ? data.detail : 'Failed to add model'
        );
      }
    } catch (err) {
      console.error('Error adding model:', err);
      toast.error('An error occurred while adding the model');
    } finally {
      if (!providerArg) setIsAddingModel(false);
    }
  };

  // Removing a custom model takes it off every profile's lists; a saved
  // choice keeps it (shown as "(removed)") until the user picks another.
  const handleDeleteModel = async (provider, modelName) => {
    if (!window.confirm(`Are you sure you want to remove "${modelName}" from ${provider}?`)) {
      return;
    }
    try {
      const response = await fetch(
        `${process.env.REACT_APP_AUTH_BACKEND_URL}/api/user/delete_model`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ provider, model: modelName }),
        }
      );

      const data = await response.json();
      if (response.ok && data.status === 'ok') {
        toast.success(data.message || 'Model deleted successfully');
        if (data.available_models) setAvailableModels(data.available_models);
        if (refreshModelSettings) refreshModelSettings();
      } else {
        toast.error(
          typeof data.detail === 'string' ? data.detail : 'Failed to delete model'
        );
      }
    } catch (err) {
      console.error('Error deleting model:', err);
      toast.error('An error occurred while deleting the model');
    }
  };

  const handleSave = async () => {
    setFocusedInput(null);

    // At least one default sub-agent (Aeri/Capo/Cardian) must stay enabled;
    // disabling all three would leave the agent pool empty in every mode.
    const allDefaultsDisabled = DEFAULT_AGENT_NAMES.every(
      (name) => isAgentDisabled(agentModelAssignments[name])
    );
    if (allDefaultsDisabled) {
      toast.error(
        'At least one default agent (Aeri, Capo, or Cardian) must stay enabled.'
      );
      return;
    }

    const providerConfigsToSave = {};
    const changedConfigLabels = [];

    for (const [provider, cfgList] of Object.entries(providerConfigs)) {
      const originalList = originalProviderConfigs[provider] || [];
      const toSave = cfgList
        .map(cfg => {
          const orig = originalList.find(c => c.config_index === cfg.config_index) || {};
          const credentialPayload = {};
          let credentialsChanged = false;

          for (const field of CREDENTIAL_FIELDS) {
            const currentValue = normalizeFieldValue(field, cfg[field] || '');
            const originalValue = orig[field] || '';

            if (currentValue && isMasked(currentValue)) continue;
            if (currentValue === '') {
              if (originalValue !== '') {
                credentialPayload[field] = null;
                credentialsChanged = true;
              }
              continue;
            }
            if (currentValue !== originalValue) {
              credentialPayload[field] = currentValue;
              credentialsChanged = true;
            }
          }

          const currentConfigName = (cfg.config_name || '').trim();
          const originalConfigName = (orig.config_name || '').trim();
          const configNameChanged = currentConfigName !== originalConfigName;

          if (!credentialsChanged && !configNameChanged) return null;

          if (credentialsChanged || configNameChanged) {
            const baseName = provider.charAt(0).toUpperCase() + provider.slice(1);
            const numSuffix = cfg.config_index > 1 ? ` (${cfg.config_index})` : '';
            const nameStr = cfg.config_name ? ` "${cfg.config_name}"` : '';
            changedConfigLabels.push(`${baseName}${numSuffix}${nameStr}`);
          }

          const payloadCfg = { config_index: cfg.config_index, ...credentialPayload };
          if (configNameChanged) {
            payloadCfg.config_name = currentConfigName === '' ? null : cfg.config_name;
          }
          return payloadCfg;
        })
        .filter(Boolean);
      if (toSave.length > 0) providerConfigsToSave[provider] = toSave;
    }

    const agentAssignmentsToSave = {};
    for (const agentName of Object.keys(agentModelAssignments)) {
      const current = agentModelAssignments[agentName];
      const original = originalAgentModelAssignments[agentName];
      if (JSON.stringify(current) !== JSON.stringify(original)) {
        agentAssignmentsToSave[agentName] = current;
      }
    }
    for (const agentName of Object.keys(originalAgentModelAssignments)) {
      if (!DEFAULT_AGENT_NAMES.includes(agentName) && !(agentName in agentModelAssignments)) {
        agentAssignmentsToSave[agentName] = null;
      }
    }

    const personasToSave = personas.map((persona, idx) => ({
      id: persona.id || '',
      sort_order: persona.sort_order ?? (idx + 1),
      title: (persona.title || '').trim(),
      prompt: (persona.prompt || '').trim(),
    }));
    const hasIncompletePersona = personasToSave.some((persona) => {
      const hasTitle = Boolean(persona.title);
      const hasPrompt = Boolean(persona.prompt);
      return hasTitle !== hasPrompt;
    });
    if (hasIncompletePersona) {
      toast.error('Each persona needs both a title and a prompt (or leave both empty).');
      return;
    }
    const filteredPersonasToSave = personasToSave.filter((persona) => persona.title && persona.prompt);

    const originalPersonasToCompare = originalPersonas
      .map((persona, idx) => ({
        id: persona.id || '',
        sort_order: persona.sort_order ?? (idx + 1),
        title: (persona.title || '').trim(),
        prompt: (persona.prompt || '').trim(),
      }))
      .filter((persona) => persona.title && persona.prompt);
    const personasChanged = JSON.stringify(filteredPersonasToSave) !== JSON.stringify(originalPersonasToCompare);

    let finalOrchestratorProvider = orchestratorProvider;
    let finalOrchestratorModel = orchestratorModel;
    if (finalOrchestratorModel === '__ADD_NEW__') finalOrchestratorModel = '';

    const orchestratorModelToSave = {
      provider: finalOrchestratorProvider,
      model: finalOrchestratorModel,
      name: orchestratorModelName || `${finalOrchestratorProvider}:${finalOrchestratorModel}`,
      max_tokens: orchestratorSettings.maxTokens ? parseInt(orchestratorSettings.maxTokens) : null,
      temperature: orchestratorSettings.temperature ? parseFloat(orchestratorSettings.temperature) : null,
      top_p: orchestratorSettings.topP ? parseFloat(orchestratorSettings.topP) : null,
      timeout: orchestratorSettings.timeout ? parseInt(orchestratorSettings.timeout) : null,
      presence_penalty: orchestratorSettings.presencePenalty ? parseFloat(orchestratorSettings.presencePenalty) : null,
      frequency_penalty: orchestratorSettings.frequencyPenalty ? parseFloat(orchestratorSettings.frequencyPenalty) : null,
    };

    const body = {
      user_id: user.id,
      // Save the model selections into the profile this tab is editing
      // (the active one); provider configs and personas stay global.
      profile_name: activeProfileName,
      provider_configs: providerConfigsToSave,
      agent_model_assignments: agentAssignmentsToSave,
    };

    if (personasChanged) body.personas = filteredPersonasToSave;

    if ((orchestratorProvider !== originalOrchestratorProvider || orchestratorModel !== originalOrchestratorModel) && finalOrchestratorModel) {
      body.orchestrator_model = orchestratorModelToSave;
    }

    // Summarizer override: only send when changed.  An empty provider (or
    // missing model) means "Use main model" and clears any stored override.
    let finalSummarizerModel = summarizerModel;
    if (finalSummarizerModel === '__ADD_NEW__') finalSummarizerModel = '';
    const summarizerChanged =
      summarizerProvider !== originalSummarizerProvider ||
      summarizerModel !== originalSummarizerModel;
    if (summarizerChanged) {
      body.summarizer_model = (summarizerProvider && finalSummarizerModel)
        ? {
            use_main_model: false,
            provider: summarizerProvider,
            model: finalSummarizerModel,
          }
        : { use_main_model: true };
    }

    const savePromise = fetch(`${process.env.REACT_APP_AUTH_BACKEND_URL}/api/user/api_keys`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      credentials: 'include',
    }).then(response => {
      if (!response.ok) {
        return response.json().then(err => Promise.reject(err.detail || 'Failed to save.'));
      }
      return response.json();
    });

    const privacyChanged = optOutTraining !== originalOptOutTraining || optOutResearch !== originalOptOutResearch;
    let privacyPromise = Promise.resolve({ status: 'ok' });

    if (privacyChanged) {
      privacyPromise = fetch(`${process.env.REACT_APP_AUTH_BACKEND_URL}/api/user/privacy_preferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ opt_out_training: optOutTraining, opt_out_research: optOutResearch }),
        credentials: 'include',
      }).then(response => {
        if (!response.ok) {
          return response.json().then(err => Promise.reject(err.detail || 'Failed to save privacy preferences.'));
        }
        return response.json();
      });
    }

    toast.promise(Promise.all([savePromise, privacyPromise]), {
      loading: 'Saving...',
      success: ([saveData, _privacyData]) => {
        setProviderConfigs(prev => {
          const next = {};
          for (const [prov, cfgList] of Object.entries(prev)) {
            next[prov] = cfgList.map(cfg => maskSavedConfig(cfg));
          }
          setOriginalProviderConfigs(next);
          return next;
        });

        setOriginalAgentModelAssignments(JSON.parse(JSON.stringify(agentModelAssignments)));
        if (saveData?.personas) {
          const normalizedPersonas = normalizePersonasForState(saveData.personas);
          setPersonas(normalizedPersonas);
          setOriginalPersonas(JSON.parse(JSON.stringify(normalizedPersonas)));
        } else if (personasChanged) {
          const normalized = normalizePersonasForState(filteredPersonasToSave);
          setPersonas(normalized);
          setOriginalPersonas(JSON.parse(JSON.stringify(normalized)));
        }
        setOriginalOrchestratorProvider(finalOrchestratorProvider);
        setOriginalOrchestratorModel(finalOrchestratorModel);
        if (summarizerChanged) {
          const clearedSummarizer = !(summarizerProvider && finalSummarizerModel);
          setSummarizerProvider(clearedSummarizer ? '' : summarizerProvider);
          setSummarizerModel(clearedSummarizer ? '' : finalSummarizerModel);
          setOriginalSummarizerProvider(clearedSummarizer ? '' : summarizerProvider);
          setOriginalSummarizerModel(clearedSummarizer ? '' : finalSummarizerModel);
        }

        if (privacyChanged) {
          setOriginalOptOutTraining(optOutTraining);
          setOriginalOptOutResearch(optOutResearch);
        }

        if (refreshModelSettings) refreshModelSettings();

        const savedItems = [...changedConfigLabels];
        if (Object.keys(agentAssignmentsToSave).length > 0) savedItems.push('sub-agent assignments');
        if (personasChanged) savedItems.push('personas');
        if (body.orchestrator_model) savedItems.push('orchestrator model');
        if (body.summarizer_model) savedItems.push('summarizer model');
        if (privacyChanged) savedItems.push('privacy preferences');
        return savedItems.length > 0
          ? `Saved: ${savedItems.join(', ')}`
          : 'Settings saved (no changes detected)';
      },
      error: (err) => `Error: ${err.toString()}`,
    });
  };

  // ---------------------------------------------------------------------------
  // Connect tab handlers
  // ---------------------------------------------------------------------------

  const fetchConnectDevices = async () => {
    setConnectLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_AUTH_BACKEND_URL}/connect/devices`,
        { credentials: 'include' }
      );
      const data = await res.json();
      if (res.ok && data.devices) {
        setConnectDevices(data.devices);
        setConnectLoaded(true);
      } else {
        toast.error(data.detail || 'Failed to load devices.');
      }
    } catch {
      toast.error('Could not reach the Connect service.');
    } finally {
      setConnectLoading(false);
    }
  };

  const handleCreateConnectToken = async (deviceName) => {
    setIsCreatingToken(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_AUTH_BACKEND_URL}/connect/tokens`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ device_name: deviceName }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Failed to generate token.');
        return null;
      }
      await fetchConnectDevices();
      return data;
    } catch {
      toast.error('Failed to generate token.');
      return null;
    } finally {
      setIsCreatingToken(false);
    }
  };

  const handleRevokeConnectToken = async (tokenId, deviceName) => {
    if (!window.confirm(`Revoke token for "${deviceName}"? The device will disconnect immediately.`)) return;
    setIsRevokingId(tokenId);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_AUTH_BACKEND_URL}/connect/tokens/${tokenId}`,
        { method: 'DELETE', credentials: 'include' }
      );
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Failed to revoke token.');
        return;
      }
      toast.success(`Token for "${deviceName}" revoked.`);
      setConnectDevices(prev => prev.filter(d => d.token_id !== tokenId));
    } catch {
      toast.error('Failed to revoke token.');
    } finally {
      setIsRevokingId(null);
    }
  };

  const handleDeleteAllConversations = async () => {
    if (!window.confirm(
      'Are you sure you want to delete ALL your conversation history from the database? This action cannot be undone.'
    )) return;

    setIsDeletingAll(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_AUTH_BACKEND_URL}/delete_all_chats`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const data = await response.json();
      if (response.ok && data.status === 'ok') {
        toast.success(`Successfully deleted ${data.count} conversation(s) from database`);
        if (resetMainPanel) resetMainPanel();
        if (refreshHistories) refreshHistories();
      } else {
        toast.error(data.detail || 'Failed to delete conversations.');
      }
    } catch (err) {
      toast.error('An error occurred while deleting conversations.');
      console.error('Error deleting all conversations:', err);
    } finally {
      setIsDeletingAll(false);
    }
  };

  const handleDeleteRawAudits = async () => {
    if (!window.confirm(
      'Are you sure you want to delete ALL your raw conversation audit files from cloud storage? This action cannot be undone.'
    )) return;

    setIsDeletingRawAudits(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_AUTH_BACKEND_URL}/delete_all_raw_audits`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const data = await response.json();
      if (response.ok && data.status === 'ok') {
        toast.success(`Successfully deleted ${data.count} raw audit file(s) from cloud storage`);
      } else {
        toast.error(data.detail || 'Failed to delete raw audit files.');
      }
    } catch (err) {
      toast.error('An error occurred while deleting raw audit files.');
      console.error('Error deleting raw audits:', err);
    } finally {
      setIsDeletingRawAudits(false);
    }
  };

  // The provider dropdowns' entries: the list the left panel and the
  // workflow builder show.
  const providerConfigKeys = buildConfiguredProviderOptions(
    providerConfigs,
  ).map(({ value, label }) => ({ key: value, label }));

  const configuredProviders = API_PROVIDERS.filter(provider =>
    (providerConfigs[provider] || []).some(cfg =>
      cfg.api_key || cfg.access_key || cfg.secret_key ||
      (provider === 'local' && cfg.endpoint)
    )
  );

  useEffect(() => {
    if (!selectedApiProvider || !API_PROVIDERS.includes(selectedApiProvider)) {
      setSelectedApiProvider(configuredProviders[0] || API_PROVIDERS[0] || '');
    }
  }, [selectedApiProvider, configuredProviders]);

  // Lazy-load connect devices the first time the Connect or Model Defaults
  // tab is opened (Model Defaults uses device models for local dropdowns).
  useEffect(() => {
    if (
      (activeTab === 'connect' || activeTab === 'model-defaults') &&
      !connectLoaded && !connectLoading
    ) {
      void fetchConnectDevices();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (orchestratorModel === '__ADD_NEW__' && orchestratorProvider && orchestratorProvider !== 'default') {
      if (addModelProvider !== orchestratorProvider) {
        setAddModelProvider(orchestratorProvider);
        setAddModelName('');
      }
    } else {
      if (addModelProvider) {
        setAddModelProvider('');
        setAddModelName('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orchestratorModel, orchestratorProvider]);

  const getInputValue = (fieldVal, focusKey) => {
    if (focusedInput === focusKey && isMasked(fieldVal)) return '';
    return fieldVal || '';
  };

  if (!user) {
    return (
      <div className={styles.userPageCard}>
        <p>Please log in to view your profile.</p>
      </div>
    );
  }

  return (
    <div className={styles.userPageCard}>
      <h1 className={styles.userPageTitle}>User Profile</h1>

      <div className={styles.userInfo} style={{ marginBottom: '1rem' }}>
        <p className={styles.userName}>{user.name}</p>
        <p className={styles.userEmail}>{user.email}</p>
        <p className={styles.userEmail}>{user.id.split(':')[1]}</p>
      </div>

      <TabNav activeTab={activeTab} setActiveTab={setActiveTab} />

      <div style={{ height: '400px', overflowY: 'auto', overflowX: 'hidden', paddingRight: '0.5rem', boxSizing: 'border-box' }}>

        {activeTab === 'api-keys' && (
          <ApiKeysTab
            isLoading={isLoading}
            selectedApiProvider={selectedApiProvider}
            setSelectedApiProvider={setSelectedApiProvider}
            API_PROVIDERS={API_PROVIDERS}
            configuredProviders={configuredProviders}
            providerConfigs={providerConfigs}
            handleAddProviderConfig={handleAddProviderConfig}
            handleDeleteProviderConfig={handleDeleteProviderConfig}
            handleConfigFieldChange={handleConfigFieldChange}
            handleClearConfigField={handleClearConfigField}
            getInputValue={getInputValue}
            setFocusedInput={setFocusedInput}
            isMasked={isMasked}
            PROVIDER_CREDENTIAL_FIELDS={PROVIDER_CREDENTIAL_FIELDS}
            toolApiKeys={toolApiKeys}
            toolApiKeyInputs={toolApiKeyInputs}
            setToolApiKeyInputs={setToolApiKeyInputs}
            handleSaveToolApiKey={handleSaveToolApiKey}
            handleRemoveToolApiKey={handleRemoveToolApiKey}
          />
        )}

        {activeTab === 'connect' && (
          <ConnectTab
            devices={connectDevices}
            isLoading={connectLoading}
            onRefresh={fetchConnectDevices}
            onCreateToken={handleCreateConnectToken}
            onRevokeToken={handleRevokeConnectToken}
            isCreating={isCreatingToken}
            isRevokingId={isRevokingId}
          />
        )}

        {activeTab === 'personas' && (
          <PersonasTab
            MAX_PERSONAS={MAX_PERSONAS}
            handleResetPersonasToDefault={handleResetPersonasToDefault}
            personas={personas}
            handleResetSinglePersonaToDefault={handleResetSinglePersonaToDefault}
            handleRemovePersona={handleRemovePersona}
            handlePersonaFieldChange={handlePersonaFieldChange}
          />
        )}

        {activeTab === 'model-defaults' && (
          <ModelDefaultsTab
            orchestratorProvider={orchestratorProvider}
            handleOrchestratorProviderChange={handleOrchestratorProviderChange}
            orchestratorModel={orchestratorModel}
            setOrchestratorModel={setOrchestratorModel}
            summarizerProvider={summarizerProvider}
            handleSummarizerProviderChange={handleSummarizerProviderChange}
            summarizerModel={summarizerModel}
            setSummarizerModel={setSummarizerModel}
            addModelProvider={addModelProvider}
            setAddModelProvider={setAddModelProvider}
            addModelName={addModelName}
            setAddModelName={setAddModelName}
            isAddingModel={isAddingModel}
            handleAddModel={handleAddModel}
            handleDeleteModel={handleDeleteModel}
            availableModels={availableModels}
            modelCatalog={modelCatalog}
            modelRecommended={modelRecommended}
            providerConfigKeys={providerConfigKeys}
            providerConfigs={providerConfigs}
            agentModelAssignments={agentModelAssignments}
            setAgentModelAssignments={setAgentModelAssignments}
            profiles={profiles}
            activeProfileName={activeProfileName}
            switchProfile={switchProfile}
            createProfile={createProfile}
            renameProfile={renameProfile}
            deleteProfile={deleteProfile}
            isModelDirty={modelDefaultsDirty}
            personas={personas}
            connectDevices={connectDevices}
            connectDevicesLoaded={connectLoaded}
          />
        )}

        {activeTab === 'account' && (
          <AccountTab
            API_URL={process.env.REACT_APP_AUTH_BACKEND_URL}
            onLoggedOut={logout}
            optOutTraining={optOutTraining}
            setOptOutTraining={setOptOutTraining}
            optOutResearch={optOutResearch}
            setOptOutResearch={setOptOutResearch}
            isDeletingAll={isDeletingAll}
            handleDeleteAllConversations={handleDeleteAllConversations}
            isDeletingRawAudits={isDeletingRawAudits}
            handleDeleteRawAudits={handleDeleteRawAudits}
          />
        )}

      </div>

      <div className={styles.messageContainer} />

      <div className={styles.buttonContainer} style={{ marginTop: '0.25rem' }}>
        <button onClick={() => void handleSave()} className={styles.button}>
          Save
        </button>
        {activeTab !== 'account' && (
          <button onClick={logout} className={styles.button} style={{ backgroundColor: '#e5393f' }}>
            Logout
          </button>
        )}
      </div>
    </div>
  );
}
